import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import { Grid, Paper, TextField, Typography } from '@material-ui/core';

import { useMutation, useQuery } from '@apollo/client';

import { Button } from 'core/components/button';
import PaymentMethodFields from './payment-method-fields';
import { useSweepPaymentToken } from './use-sweep-payment-token';
import { CREATE_SWEEP_ACCOUNT, GET_SWEEP_ACCOUNT_PREFILL } from './sweep-account.queries';
import { buildPrefillInitialValues, isFieldLocked } from './setup-form.helpers';
import useStyles from './sweep-account.styles';

const validate = (values) => {
  const errors = {};
  if (!values.name) {
    errors.name = 'Required';
  }
  if (!values.email) {
    errors.email = 'Required';
  }
  return errors;
};

const buildContact = (values) => ({
  name: values.name,
  email: values.email,
  phone: values.phone || null,
  address: values.line1
    ? {
        line1: values.line1,
        line2: values.line2 || null,
        city: values.city,
        state: values.state,
        zip: values.zip
      }
    : null
});

const SetupForm = ({ onCreated }) => {
  const classes = useStyles();
  const [createSweepAccount] = useMutation(CREATE_SWEEP_ACCOUNT);
  const getPaymentToken = useSweepPaymentToken();
  const [error, setError] = useState(null);
  // Pre-fill from the agency record we already hold (#33822) — a best-effort enrichment that
  // must never block the form. We render immediately with blank/editable defaults and patch in
  // the known (locked) values once the query resolves (enableReinitialize below).
  const { data: prefillData } = useQuery(GET_SWEEP_ACCOUNT_PREFILL, { fetchPolicy: 'no-cache' });
  const prefill = prefillData?.getSweepAccountPrefill ?? null;

  const onSubmit = async (values, { setSubmitting }) => {
    setError(null);
    try {
      const { paymentMethodToken, paymentMethodType } = await getPaymentToken(values.paymentMethodType);
      const { data } = await createSweepAccount({
        variables: { contact: buildContact(values), paymentMethodToken, paymentMethodType }
      });
      onCreated(data.createSweepAccount);
    } catch (e) {
      setError(e.message || 'Failed to create sweep account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={buildPrefillInitialValues(prefill)}
      validate={validate}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
        <Form>
          <Paper className={classes.card} elevation={1}>
            <Typography variant="h6" gutterBottom>
              Agency details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="name"
                  label="Agency name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isFieldLocked('name', prefill)}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="email"
                  label="Email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth name="phone" label="Phone" value={values.phone} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="line1"
                  label="Address line 1"
                  value={values.line1}
                  onChange={handleChange}
                  disabled={isFieldLocked('line1', prefill)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth name="line2" label="Address line 2" value={values.line2} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  name="city"
                  label="City"
                  value={values.city}
                  onChange={handleChange}
                  disabled={isFieldLocked('city', prefill)}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  name="state"
                  label="State"
                  value={values.state}
                  onChange={handleChange}
                  disabled={isFieldLocked('state', prefill)}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  name="zip"
                  label="ZIP"
                  value={values.zip}
                  onChange={handleChange}
                  disabled={isFieldLocked('zip', prefill)}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper className={classes.card} elevation={1}>
            <Typography variant="h6" gutterBottom>
              Payment method
            </Typography>
            <PaymentMethodFields />
          </Paper>

          {error && (
            <Typography color="error" className={classes.error}>
              {error}
            </Typography>
          )}
          <div className={classes.actions}>
            <Button type="submit" variant="contained" color="primary" loading={isSubmitting}>
              Create sweep account
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

SetupForm.propTypes = {
  onCreated: PropTypes.func.isRequired
};

export default SetupForm;
